import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/errors";
import { Banner } from "../components/ui";

// Every /v1/auth/* route core-platform rate limits (login, MFA verify,
// step-up, signup, recovery) answers a denied attempt with
// `429 {"error":"rate_limited"}` and a Retry-After header in seconds
// (cmd/core-platform/auth_ratelimit.go deny). The body is identical for
// every bucket, and per-account buckets are charged whether or not the
// account exists, so the screen says the same thing in every case too.
//
// useRateLimit() turns such an error into a countdown; the screen shows
// <RateLimitNotice> and disables its submit buttons until it reaches 0.

/** Used when a 429 arrives without a usable Retry-After. */
export const DEFAULT_RETRY_AFTER_SECONDS = 30;

export interface RateLimitState {
  /** True while the wait is running. */
  limited: boolean;
  /** Whole seconds left (0 when not limited). */
  remaining: number;
  /** When the wait ends (ms since epoch), or null. */
  until: number | null;
  /** If err is a 429, start the countdown and return true; otherwise return false. */
  capture: (err: unknown) => boolean;
}

export function isRateLimited(err: unknown): err is ApiError {
  return err instanceof ApiError && err.kind === "rate_limited";
}

export function useRateLimit(): RateLimitState {
  const [until, setUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until === null) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) setUntil(null);
    }, 1000);
    return () => clearInterval(id);
  }, [until]);

  const capture = useCallback((err: unknown): boolean => {
    if (!isRateLimited(err)) return false;
    const secs = err.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
    const t = Date.now();
    setNow(t);
    setUntil(t + secs * 1000);
    return true;
  }, []);

  const remaining = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
  return { limited: remaining > 0, remaining, until: remaining > 0 ? until : null, capture };
}

/** "45 seconds", "1 minute 5 seconds", "2 hours 3 minutes". */
export function formatWait(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;
  if (s < 60) return plural(s, "second");
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${plural(m, "minute")} ${plural(rest, "second")}` : plural(m, "minute");
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${plural(h, "hour")} ${plural(m, "minute")}` : plural(h, "hour");
}

/**
 * The wait message. The announcement happens once, when the banner
 * appears (role="alert"); the ticking countdown is a role="timer", which
 * screen readers do not re-announce every second.
 */
export function RateLimitNotice({ state }: { state: RateLimitState }) {
  if (!state.limited || state.until === null) return null;
  const long = state.remaining > 300;
  const at = new Date(state.until).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <div role="alert">
      <Banner tone="warning" title="Too many attempts" icon="clock">
        For your security, this is paused. You can try again in{" "}
        <strong role="timer">{formatWait(state.remaining)}</strong>
        {long ? ` (at ${at})` : ""}.
      </Banner>
    </div>
  );
}
